import { zodResolver } from '@hookform/resolvers/zod'
import {
    Button,
    Checkbox,
    Form,
    FormTextField,
    Icons,
    Label,
    Skeleton,
} from '@longy/web-components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { BookingStep } from '../../components/booking/BookingStep'
import { CouponField } from '../../components/booking/CouponField'
import { TaxBreakdown } from '../../components/checkout/TaxBreakdown'
import { api, getTRPCErrorMessage } from '../../core/trpc'
import { useAuth } from '../../hooks/useAuth'
import { useLocale } from '../../hooks/useLocale'
import {
    buildCheckoutBillingDefaults,
    buildPersonalBilling,
    EMPTY_BILLING_FORM,
    toCheckoutBillingFields,
    toProfileUpdate,
} from '../../utils/billingDefaults'
import { formatPrice } from '../../utils/formatPrice'
import { isFreeRegistration } from '../../utils/freeCheckout'

const PATH = '/booking/checkout'

const checkoutSearchSchema = z.object({
    purchasableItemId: z.string(),
    transactionId: z.string().optional(),
    cancelled: z.string().optional(),
})

/**
 * The billing fields are mandatory because the payment worker refuses a web transaction without
 * them — it has an invoice to issue and nowhere to send it.
 */
const billingFormSchema = z.object({
    billingName: z.string().min(1),
    billingPostalCode: z.string().min(1),
    billingCity: z.string().min(1),
    billingStreet: z.string().min(1),
    billingTaxNumber: z.string().optional(),
    phone: z.string().optional(),
})

type BillingFormSchema = z.infer<typeof billingFormSchema>

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-2 last:border-0">
        <span className="text-brand-gray">{label}</span>
        <span className="text-right font-semibold">{value}</span>
    </div>
)

const BookingCheckoutPage = () => {
    const { t, i18n } = useTranslation()
    const { purchasableItemId, transactionId, cancelled } = Route.useSearch()
    const { user, utils } = useAuth()
    const { getLocalizedValue } = useLocale()

    const [error, setError] = useState<string | null>(null)
    const [isCompany, setIsCompany] = useState(false)
    const [acceptsTerms, setAcceptsTerms] = useState(false)
    const [acceptsPaymentObligation, setAcceptsPaymentObligation] = useState(false)
    const [couponCode, setCouponCode] = useState<string | undefined>()
    const [sameAsPersonal, setSameAsPersonal] = useState(true)
    const [saveToProfile, setSaveToProfile] = useState(false)

    const {
        data: summary,
        isPending,
        isFetching,
        error: summaryError,
    } = api.webCheckout.getAppointmentCheckoutSummary.useQuery({
        purchasableItemId,
        couponCode,
    })

    const { mutateAsync: initCheckout, isPending: isSubmitting } =
        api.webCheckout.initCheckout.useMutation()
    const { mutate: cancelCheckout } = api.webCheckout.cancelCheckout.useMutation()
    const { mutateAsync: saveBillingProfile } = api.user.editSelf.useMutation()

    const releasedRef = useRef(false)

    useEffect(() => {
        // Coming back from Stripe without paying: hand the held seat back at once rather than
        // making the next person wait out the hold window.
        if (!cancelled || !transactionId || releasedRef.current) return

        releasedRef.current = true
        cancelCheckout({ transactionId })
    }, [cancelled, transactionId, cancelCheckout])

    const form = useForm<BillingFormSchema>({
        resolver: zodResolver(billingFormSchema),
        // The account is not loaded on the first render, so the real values arrive through the
        // reset below rather than here.
        defaultValues: EMPTY_BILLING_FORM,
    })

    const billingDefaults = useMemo(() => buildCheckoutBillingDefaults(user), [user])
    const personalBilling = useMemo(() => buildPersonalBilling(user), [user])
    const prefilled = useRef(false)

    useEffect(() => {
        // Once, when the account first arrives: somebody who has billing details on file should
        // not type them again. Re-running on every `getMe` would overwrite what they are editing.
        if (prefilled.current || !user) return

        prefilled.current = true
        form.reset(billingDefaults.values)
        setSameAsPersonal(billingDefaults.sameAsPersonal)
        setIsCompany(!!billingDefaults.values.billingTaxNumber)
    }, [user, billingDefaults, form])

    const mirrorsPersonal = !!user && sameAsPersonal && !isCompany

    const onSameAsPersonalChange = (checked: boolean) => {
        setSameAsPersonal(checked)
        if (!checked) return

        for (const [field, value] of Object.entries(personalBilling)) {
            if (value) {
                form.setValue(field as keyof typeof personalBilling, value, {
                    shouldValidate: true,
                })
            }
        }
    }

    const isMirrored = (field: keyof typeof personalBilling) =>
        mirrorsPersonal && !!personalBilling[field]

    const watchedValues = form.watch()
    const pendingProfileUpdate = user
        ? toProfileUpdate(
              {
                  ...watchedValues,
                  billingTaxNumber: isCompany ? watchedValues.billingTaxNumber : undefined,
              },
              user,
              { sameAsPersonal: mirrorsPersonal, includesBilling: true },
          )
        : null

    // A code that priced nothing is not sent on to the checkout, which refuses an invalid one.
    const isCouponApplied = Boolean(couponCode) && summary?.couponValid !== false
    const hasDiscount = summary ? summary.price < summary.originalPrice : false

    /**
     * Nothing to charge — an entitlement covers it, the service is priced at 0, or a discount or
     * coupon took the whole amount off. The server already treats this as a registration rather
     * than a purchase: no Stripe session, no invoice, no billing address. The page has to ask the
     * same, or it collects consent to a payment obligation that does not exist and blocks the
     * booking behind an address nothing will ever use.
     */
    const isFree = isFreeRegistration({
        hasResolvedPrice: !!summary,
        totalPrice: summary?.price ?? 0,
    })

    const onSubmit = async (data: BillingFormSchema) => {
        setError(null)

        const profileUpdate =
            saveToProfile && user
                ? toProfileUpdate(
                      { ...data, billingTaxNumber: isCompany ? data.billingTaxNumber : undefined },
                      user,
                      { sameAsPersonal: mirrorsPersonal, includesBilling: true },
                  )
                : null

        if (profileUpdate) {
            try {
                await saveBillingProfile(profileUpdate)
                await utils.user.getMe.invalidate()
            } catch (caught) {
                console.warn('Failed to save checkout details to the account', caught)
            }
        }

        try {
            const result = await initCheckout({
                itemType: 'appointmentSlot',
                itemId: purchasableItemId,
                quantity: 1,
                firstName: user?.firstName ?? '',
                lastName: user?.lastName ?? '',
                email: user?.email ?? '',
                phone: data.phone || undefined,
                isCompany: isFree ? false : isCompany,
                ...toCheckoutBillingFields(
                    { ...data, billingTaxNumber: isCompany ? data.billingTaxNumber : undefined },
                    isFree,
                ),
                acceptsTerms: true,
                acceptsPaymentObligation: isFree ? undefined : acceptsPaymentObligation,
                couponCode: isCouponApplied ? couponCode : undefined,
            })

            if (result.kind === 'free') {
                window.location.href = `/booking/success?transactionId=${result.transactionId}`
                return
            }

            window.location.href = result.checkoutUrl
        } catch (caught) {
            setError(getTRPCErrorMessage(caught))
        }
    }

    if (isPending) {
        return (
            <BookingStep title={t('frontend.booking.summary')} step={t('frontend.booking.title')}>
                <Skeleton className="h-96 w-full" />
            </BookingStep>
        )
    }

    /**
     * The slot is gone, full, or already this person's. Saying so here is the whole point of the
     * summary refusing: rendering the billing form under a missing summary asked somebody to type
     * an address for a booking that was never going to be made.
     */
    if (summaryError) {
        return (
            <BookingStep title={t('frontend.booking.summary')} step={t('frontend.booking.title')}>
                <div className="flex flex-col items-start gap-4 rounded-lg border border-slate-200 bg-white p-6">
                    <p className="text-destructive text-sm">{getTRPCErrorMessage(summaryError)}</p>
                    <Button asChild variant="outline">
                        <Link to="/bookings">{t('frontend.booking.backToBookings')}</Link>
                    </Button>
                </div>
            </BookingStep>
        )
    }

    // Neither pending nor failed, so the query has answered; the guard is for the type only.
    if (!summary) return null

    // Privacy consent is unconditional; the payment obligation only exists when there is one.
    const canSubmit = acceptsTerms && (isFree || acceptsPaymentObligation) && !isSubmitting

    return (
        <BookingStep title={t('frontend.booking.summary')} step={t('frontend.booking.title')}>
            <div className="flex flex-col gap-8">
                {cancelled && (
                    <p className="rounded-lg bg-amber-50 p-4 text-sm text-amber-900">
                        {t('frontend.booking.paymentCancelled')}
                    </p>
                )}

                {summary && (
                    <section className="rounded-lg border border-slate-200 bg-white p-6">
                        <SummaryRow
                            label={t('frontend.booking.service')}
                            value={summary.serviceName}
                        />
                        <SummaryRow
                            label={t('frontend.booking.expert')}
                            value={summary.expertName}
                        />
                        <SummaryRow
                            label={t('frontend.booking.dateAndTime')}
                            value={new Date(summary.startDate).toLocaleString(
                                i18n.language === 'en' ? 'en-GB' : 'hu-HU',
                            )}
                        />
                        <SummaryRow
                            label={t('frontend.booking.duration')}
                            value={`${summary.durationInMinutes} ${t('frontend.booking.minutes')}`}
                        />
                        {summary.locationName && (
                            <SummaryRow
                                label={t('frontend.booking.location')}
                                value={summary.locationName}
                            />
                        )}
                        {hasDiscount && (
                            <SummaryRow
                                label={t('frontend.booking.originalPrice')}
                                value={formatPrice(summary.originalPrice)}
                            />
                        )}
                        {hasDiscount && summary.discountName && (
                            <SummaryRow
                                label={t('frontend.booking.discountApplied')}
                                value={summary.discountName}
                            />
                        )}
                        {/*
                         * A package is not a discount row, so without this a booking a
                         * subscription covers reads as free — or as cheaper — with nothing on
                         * screen to account for it.
                         */}
                        {summary.entitlement && (
                            <SummaryRow
                                label={t(
                                    summary.entitlement.coversInFull
                                        ? 'frontend.booking.coveredByPackage'
                                        : 'frontend.booking.packageDiscount',
                                )}
                                value={[
                                    getLocalizedValue(summary.entitlement.packageName),
                                    summary.entitlement.coversInFull
                                        ? summary.entitlement.freeUsagesLeft === null
                                            ? t('frontend.booking.unlimitedUsages')
                                            : t('frontend.booking.freeUsagesLeft', {
                                                  count: summary.entitlement.freeUsagesLeft,
                                              })
                                        : null,
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                            />
                        )}
                        <SummaryRow
                            label={t('frontend.booking.price')}
                            value={
                                isFree ? t('common.ui.freeOfCharge') : formatPrice(summary.price)
                            }
                        />
                        <TaxBreakdown
                            tax={summary.tax}
                            totalPrice={summary.price}
                            className="mt-3 flex flex-col gap-2 border-t border-slate-100 pt-3"
                        />
                    </section>
                )}

                {/* A coupon has nothing left to take off a booking that already costs nothing. */}
                {(!isFree || couponCode) && (
                    <CouponField
                        appliedCode={couponCode}
                        isInvalid={summary?.couponValid === false}
                        isPending={isFetching}
                        onApply={setCouponCode}
                        onRemove={() => setCouponCode(undefined)}
                    />
                )}

                {/*
                 * A free booking issues no invoice, so there is no address to put on one. The app
                 * skips its billing form for the same reason — see `withBillingGateway`.
                 */}
                {!isFree && (
                    <section className="flex flex-col gap-4">
                        <h2 className="text-lg font-semibold">
                            {t('frontend.booking.billingDetails')}
                        </h2>
                        {user && !isCompany && (
                            <div className="flex items-center gap-3">
                                <Checkbox
                                    id="same-as-personal"
                                    checked={sameAsPersonal}
                                    onCheckedChange={(value) =>
                                        onSameAsPersonalChange(value === true)
                                    }
                                />
                                <Label htmlFor="same-as-personal">
                                    {t('frontend.booking.sameAsPersonal')}
                                </Label>
                            </div>
                        )}
                        <Form {...form}>
                            <FormTextField
                                control={form.control}
                                name="billingName"
                                label={t('frontend.booking.billingName')}
                                inputProps={{ readOnly: isMirrored('billingName') }}
                            />
                            <FormTextField
                                control={form.control}
                                name="billingPostalCode"
                                label={t('frontend.booking.billingPostalCode')}
                                inputProps={{ readOnly: isMirrored('billingPostalCode') }}
                            />
                            <FormTextField
                                control={form.control}
                                name="billingCity"
                                label={t('frontend.booking.billingCity')}
                                inputProps={{ readOnly: isMirrored('billingCity') }}
                            />
                            <FormTextField
                                control={form.control}
                                name="billingStreet"
                                label={t('frontend.booking.billingStreet')}
                                inputProps={{ readOnly: isMirrored('billingStreet') }}
                            />
                            <FormTextField
                                control={form.control}
                                name="phone"
                                label={t('frontend.booking.phone')}
                            />
                        </Form>

                        <div className="flex items-center gap-3">
                            <Checkbox
                                id="is-company"
                                checked={isCompany}
                                onCheckedChange={(value) => setIsCompany(value === true)}
                            />
                            <Label htmlFor="is-company">{t('frontend.booking.isCompany')}</Label>
                        </div>

                        {isCompany && (
                            <Form {...form}>
                                <FormTextField
                                    control={form.control}
                                    name="billingTaxNumber"
                                    label={t('frontend.booking.billingTaxNumber')}
                                />
                            </Form>
                        )}

                        {pendingProfileUpdate && (
                            <div className="flex items-center gap-3">
                                <Checkbox
                                    id="save-to-profile"
                                    checked={saveToProfile}
                                    onCheckedChange={(value) => setSaveToProfile(value === true)}
                                />
                                <Label htmlFor="save-to-profile">
                                    {t('frontend.booking.saveToProfile')}
                                </Label>
                            </div>
                        )}
                    </section>
                )}

                <section className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                        <Checkbox
                            id="accept-terms"
                            checked={acceptsTerms}
                            onCheckedChange={(value) => setAcceptsTerms(value === true)}
                        />
                        <Label htmlFor="accept-terms">{t('frontend.booking.acceptTerms')}</Label>
                    </div>
                    {!isFree && (
                        <div className="flex items-center gap-3">
                            <Checkbox
                                id="accept-payment"
                                checked={acceptsPaymentObligation}
                                onCheckedChange={(value) =>
                                    setAcceptsPaymentObligation(value === true)
                                }
                            />
                            <Label htmlFor="accept-payment">
                                {t('frontend.booking.acceptPaymentObligation')}
                            </Label>
                        </div>
                    )}
                </section>

                {error && <p className="text-sm text-destructive">{error}</p>}

                <Button
                    disabled={!canSubmit}
                    onClick={
                        // Nothing is being charged and the billing fields are not on screen, so
                        // there is no billing form to validate — only a booking to make.
                        isFree ? () => onSubmit(form.getValues()) : form.handleSubmit(onSubmit)
                    }
                >
                    {isSubmitting && <Icons.Spinner className="mr-2 h-4 animate-spin" />}
                    {isFree ? t('common.booking.bookAppointment') : t('frontend.booking.payNow')}
                </Button>
            </div>
        </BookingStep>
    )
}

export const Route = createFileRoute(PATH)({
    validateSearch: checkoutSearchSchema,
    head: () => ({ meta: [{ title: 'Long-Y - Fizetés' }] }),
    component: BookingCheckoutPage,
})
